<script setup>
// Addendum §7 Tabs — VIEWS OF ONE OBJECT (Client → Details / Buckets / People /
// Rates). Sidebar sub-nav is for separate surfaces; tabs never nest.
// Roving tabindex + MANUAL activation: arrow keys move focus, Enter/Space
// activates. Manual (not automatic) because a tab that triggers a fetch on mere
// focus is a screen-reader trap. Controlled via v-model; the host renders the
// active panel (with role="tabpanel" + aria-labelledby).
import { ref, computed } from 'vue'
import { useId } from 'vue'

const props = defineProps({
  // [{ value, label, disabled?, separatorBefore? }]
  // `separatorBefore` draws a divider ahead of that tab, for splitting a long
  // list into groups. It's a border on the tab itself rather than an extra
  // element, so the tablist stays a flat list of tabs for assistive tech.
  tabs: { type: Array, required: true },

  // horizontal (default) | vertical. Vertical stacks the tabs into a left-hand
  // rail — for when there are more tabs than read comfortably in a row, or the
  // labels are long. Arrow keys work either way (Up/Left previous, Down/Right next).
  orientation: { type: String, default: 'horizontal' },
})
const model = defineModel({ default: null })
const baseId = useId()
const tablist = ref(null)

const activeIndex = computed(() => props.tabs.findIndex((t) => t.value === model.value))
const tabId = (v) => `${baseId}-tab-${v}`
const panelId = (v) => `${baseId}-panel-${v}`
defineExpose({ tabId, panelId })

function focusable() {
  return tablist.value ? [...tablist.value.querySelectorAll('[role="tab"]:not([disabled])')] : []
}
function onKey(e) {
  const els = focusable()
  const cur = els.indexOf(document.activeElement)
  if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
    e.preventDefault(); els[(cur + 1) % els.length]?.focus()
  } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
    e.preventDefault(); els[(cur - 1 + els.length) % els.length]?.focus()
  } else if (e.key === 'Home') {
    e.preventDefault(); els[0]?.focus()
  } else if (e.key === 'End') {
    e.preventDefault(); els[els.length - 1]?.focus()
  }
  // Enter/Space handled by the button's native click → activate()
}
function activate(t) {
  if (t.disabled) return
  model.value = t.value
}
</script>

<template>
  <div ref="tablist" class="l-tabs" :class="'is-' + orientation" role="tablist"
    :aria-orientation="orientation" @keydown="onKey">
    <button
      v-for="t in tabs"
      :key="t.value"
      :id="tabId(t.value)"
      type="button"
      role="tab"
      class="tab"
      :class="{ active: t.value === model, 'has-separator': t.separatorBefore }"
      :aria-selected="t.value === model"
      :aria-controls="panelId(t.value)"
      :tabindex="t.value === model || (activeIndex === -1 && t === tabs[0]) ? 0 : -1"
      :disabled="t.disabled"
      @click="activate(t)"
    >
      {{ t.label }}
    </button>
  </div>
</template>

<style scoped>
.l-tabs.is-horizontal {
  display: inline-flex;
  gap: 2px;
  border-bottom: 1px solid var(--line-strong);
}

.l-tabs.is-vertical {
  display: flex;
  flex-direction: column;
  gap: 2px;
  border-right: 1px solid var(--line-strong);
}
.tab {
  position: relative;
  font-family: var(--font-ui);
  font-size: var(--text-sm);
  font-weight: 500;
  color: var(--text-3);
  padding: 10px 14px;
  min-height: var(--target-min);
  background: none;
  border: 0;
  cursor: pointer;
}
.tab:hover:not(:disabled) {
  color: var(--text);
}
/* Group divider — a rule plus a little breathing room, on the leading edge for
   whichever direction the list runs. */
.is-vertical .tab.has-separator {
  margin-top: var(--sp-3);
  border-top: 1px solid var(--line);
  padding-top: calc(10px + var(--sp-3));
}
.is-horizontal .tab.has-separator {
  margin-left: var(--sp-3);
  border-left: 1px solid var(--line);
  padding-left: calc(14px + var(--sp-3));
}
.tab:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.tab.active {
  color: var(--text);
  font-weight: 600;
}
/* the active-tab marker uses the interactive colour (it IS a control) */
.is-horizontal .tab.active::after {
  content: '';
  position: absolute;
  left: 8px;
  right: 8px;
  bottom: -1px;
  height: 2px;
  border-radius: 2px 2px 0 0;
  background: var(--interactive);
}

/* Vertical tabs read as a rail: full-width hit area, label left-aligned, and the
   marker moves to the trailing edge so it sits on the border the panel hangs off. */
.is-vertical .tab {
  text-align: left;
  padding: 10px 16px 10px 12px;
}

.is-vertical .tab.active {
  background: var(--surface-sunken);
}

.is-vertical .tab.active::after {
  content: '';
  position: absolute;
  top: 6px;
  bottom: 6px;
  right: -1px;
  width: 2px;
  border-radius: 2px 0 0 2px;
  background: var(--interactive);
}
</style>
